import { useCan } from '@features/auth';
import { type ClientTarget } from '@features/clients';
import { Button, Card, IconSearch, SelectField, StatTile, WriteError } from '@shared/ui';
import { useMemo, useState } from 'react';

import { readAllRows, useBulkJobRows } from '../hooks/use-bulk-migrations';
import { dryRunExtras, extrasCsvFile } from '../model/dry-run-extras';
import { isActive } from '../model/job-progress';
import { OUTCOME_LABEL, outcomeFilterOptions, outcomeTotals } from '../model/outcome-copy';
import { exportDocument, exportFileName, searchRows } from '../model/paging';
import {
    launchBlocker,
    selectAllPlanned,
    selectedCount,
    selectNone,
    toggle,
    type Selection,
} from '../model/selection';

import { downloadCsv, downloadJson } from './download';
import { LaunchDialog } from './LaunchDialog';
import { TenantRowsTable } from './TenantRowsTable';

import type { BulkModule, JobHeader, TenantOutcome } from '../schemas/bulk-migration';

const TILE_OUTCOMES: readonly TenantOutcome[] = ['planned', 'not_migrated', 'skipped', 'error'];

/**
 * Step 3: the analysis. What would be migrated for each client, and why the
 * others would not; every migratable client ticked by default. Migrating is
 * `platform-admin`, so only that tier sees the button.
 */
export function DryRunView({
    target,
    environment,
    module,
    job,
    onLaunched,
}: {
    target: ClientTarget;
    environment: string;
    module: BulkModule;
    job: JobHeader;
    onLaunched: (launch: JobHeader) => void;
}) {
    const canLaunch = useCan('platform-admin');
    const [outcome, setOutcome] = useState('all');
    const [search, setSearch] = useState('');
    const [selection, setSelection] = useState<Selection>(selectAllPlanned());
    const [confirming, setConfirming] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState<Error | null>(null);
    const rows = useBulkJobRows(target, module.moduleId, job, outcome);

    const loaded = useMemo(() => rows.data?.pages.flatMap((page) => page.rows) ?? [], [rows.data]);
    const visible = searchRows(loaded, search);
    const totals = outcomeTotals(job.counts);
    const planned = totals.get('planned') ?? 0;
    const count = selectedCount(selection, planned);
    const blocker = launchBlocker(count);
    const extras = dryRunExtras(module.moduleId);
    const summary = extras?.summary(loaded) ?? null;

    if (isActive(job.state)) return null;

    const download = async () => {
        setDownloadError(null);
        setDownloading(true);
        try {
            const all = await readAllRows(target, module.moduleId, job.reportId);
            downloadJson(exportFileName(job), exportDocument(job, all));
        } catch (error) {
            setDownloadError(error instanceof Error ? error : new Error(String(error)));
        } finally {
            setDownloading(false);
        }
    };

    // The module's own export (Membership programs: its GL gaps), read end to end like the JSON.
    const downloadCsvGaps = async () => {
        if (extras === null) return;
        setDownloadError(null);
        setDownloading(true);
        try {
            const file = await extrasCsvFile(extras, target, job, readAllRows);
            downloadCsv(file.fileName, file.content);
        } catch (error) {
            setDownloadError(error instanceof Error ? error : new Error(String(error)));
        } finally {
            setDownloading(false);
        }
    };

    return (
        <>
            <div className="mb-4 grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-4">
                <StatTile label="Clients" value={String(job.total ?? 0)} />
                {TILE_OUTCOMES.map((key) => (
                    <StatTile
                        key={key}
                        label={OUTCOME_LABEL[key]}
                        value={String(totals.get(key) ?? 0)}
                    />
                ))}
            </div>

            <Card>
                <div className="flex flex-wrap items-center gap-3 border-b border-line-subtle px-4 py-3">
                    <span className="text-[13px] font-semibold text-content-subtle">
                        {count.toLocaleString('en-US')} of {planned.toLocaleString('en-US')}{' '}
                        migratable selected
                    </span>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                            setSelection(selectAllPlanned());
                        }}
                    >
                        Select all
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                            setSelection(selectNone());
                        }}
                    >
                        Select none
                    </Button>
                    <div className="ml-auto w-[240px] [&_label]:sr-only">
                        <SelectField
                            label="Outcome"
                            value={outcome}
                            onChange={setOutcome}
                            options={outcomeFilterOptions(job.counts)}
                        />
                    </div>
                    <label className="relative w-[220px]">
                        <span className="sr-only">Search loaded clients</span>
                        <IconSearch className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-content-muted" />
                        <input
                            type="search"
                            value={search}
                            onChange={(event) => {
                                setSearch(event.target.value);
                            }}
                            placeholder="Search loaded clients"
                            className="w-full rounded-lg border border-line bg-surface py-2 pl-9 pr-3 text-[13px] text-content outline-none placeholder:text-content-muted focus:border-brand"
                        />
                    </label>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void download()}
                        disabled={downloading}
                    >
                        {downloading ? 'Preparing…' : 'Download JSON'}
                    </Button>
                    {extras !== null && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => void downloadCsvGaps()}
                            disabled={downloading}
                        >
                            {downloading ? 'Preparing…' : extras.buttonLabel}
                        </Button>
                    )}
                    {canLaunch && (
                        <Button
                            onClick={() => {
                                setConfirming(true);
                            }}
                            disabled={blocker !== null}
                        >
                            Migrate {count.toLocaleString('en-US')} selected
                        </Button>
                    )}
                </div>
                {downloadError && (
                    <div className="px-4 pt-3">
                        <WriteError error={downloadError} />
                    </div>
                )}
                {summary !== null && (
                    <p className="px-4 pt-3 text-[12px] text-content-subtle">{summary}</p>
                )}
                {canLaunch && blocker && count > 0 && (
                    <p className="px-4 pt-3 text-[12px] text-danger-muted">{blocker}</p>
                )}
                <TenantRowsTable
                    module={module}
                    layout="analysis"
                    rows={visible}
                    isLoading={rows.isPending}
                    hasMore={rows.hasNextPage}
                    loadingMore={rows.isFetchingNextPage}
                    onLoadMore={() => void rows.fetchNextPage()}
                    selection={selection}
                    onToggle={(row) => {
                        setSelection((current) => toggle(current, row));
                    }}
                />
            </Card>

            {confirming && (
                <LaunchDialog
                    target={target}
                    environment={environment}
                    module={module}
                    dryRun={job}
                    selection={selection}
                    count={count}
                    onClose={() => {
                        setConfirming(false);
                    }}
                    onLaunched={(launch) => {
                        setConfirming(false);
                        onLaunched(launch);
                    }}
                />
            )}
        </>
    );
}
